import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ProductForm from '../../../components/productFrom/ProductForm';
import { getProduct, createProduct, updateProduct } from '../../../api';
import styles from './EditProductPage.module.scss';
import { Container, Paper, Typography, Alert, CircularProgress, Box } from '@mui/material';

export default function EditProductPage() {
    const { id } = useParams();
    const { search } = useLocation();
    const nav = useNavigate();

    const isNew = !id || String(id).toLowerCase() === 'new';

    const [current, setCurrent] = useState(null);
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState('');

    const preBrandId = new URLSearchParams(search).get('brandId') || '';

    /* ================= LOAD PRODUCT ================= */

    useEffect(() => {
        if (isNew) return;

        let alive = true;
        const ac = new AbortController();

        (async () => {
            try {
                setLoading(true);
                setError('');

                const data = await getProduct(id);

                if (!alive) return;
                setCurrent(data || null);
            } catch (e) {
                if (alive) setError(e?.message || 'Не вдалося завантажити продукт');
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
            ac.abort();
        };
    }, [isNew, id]);

    /* ================= INITIAL VALUES (STABLE) ================= */

    const memoInitialValues = useMemo(() => {
        if (isNew) return { brandId: preBrandId };
        return current || undefined;
    }, [isNew, preBrandId, current]);

    /* ================= SAVE ================= */

    async function handleSubmit(formValues) {
        try {
            setError('');

            const payload = {
                ...formValues,
                name: String(formValues.name ?? '').trim(),
            };

            if (!payload.name) {
                throw new Error('Назва продукту обовʼязкова');
            }

            if (isNew) {
                await createProduct(payload);
            } else {
                await updateProduct(String(id), payload);
            }

            nav('/products');
        } catch (e) {
            setError(e?.message || 'Помилка збереження продукту');
        }
    }

    /* ================= STATES ================= */

    if (!isNew && loading) {
        return (
            <Box className={styles.message}>
                <CircularProgress size={28} />
                <span className={styles.messageText}>Loading…</span>
            </Box>
        );
    }

    if (!isNew && !current && !loading) {
        return (
            <Box className={styles.message}>
                <Alert severity="warning" variant="outlined">
                    Продукт не знайдено
                </Alert>
            </Box>
        );
    }

    /* ================= RENDER ================= */

    return (
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
            <Paper className={styles.wrapper} elevation={1}>
                <Typography variant="h5" className={styles.title}>
                    {isNew ? 'Новий продукт' : 'Редагувати продукт'}
                </Typography>

                {error && (
                    <Alert severity="error" className={styles.error}>
                        {error}
                    </Alert>
                )}

                <ProductForm initialValues={memoInitialValues} onSubmit={handleSubmit} />
            </Paper>
        </Container>
    );
}
